// Renders avatar items (hats, colour balls, pattern balls) to cached PNG data
// URLs using one shared offscreen Three.js renderer — so the customise grid can
// show real glossy 3D thumbnails (like the mockup) as plain <img> tags instead
// of 18 live canvases.
import * as THREE from "three";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import type { HatId, PatternId } from "@shared/types";
import { buildHat } from "../game/engine/runnerCharacter";
import { isLowQuality } from "../game/engine/quality";

const SIZE = 192;
let renderer: THREE.WebGLRenderer | null = null;
let scene: THREE.Scene | null = null;
let camera: THREE.PerspectiveCamera | null = null;

function ensure(): { renderer: THREE.WebGLRenderer; scene: THREE.Scene; camera: THREE.PerspectiveCamera } | null {
  // Low tier: no offscreen item renders — the profile editor falls back to its
  // CSS thumbnails instead of a WebGL context on weak GPUs.
  if (isLowQuality()) return null;
  if (renderer && scene && camera) return { renderer, scene, camera };
  try {
    const c = document.createElement("canvas");
    c.width = c.height = SIZE;
    renderer = new THREE.WebGLRenderer({ canvas: c, antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setPixelRatio(1);
    renderer.setSize(SIZE, SIZE, false);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;

    scene = new THREE.Scene();
    try {
      const pmrem = new THREE.PMREMGenerator(renderer);
      scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
      scene.environmentIntensity = 0.8;
      pmrem.dispose();
    } catch { /* reflections optional */ }
    scene.add(new THREE.HemisphereLight(0xffffff, 0xd0d8ff, 0.9));
    const key = new THREE.DirectionalLight(0xfff2d8, 2.2);
    key.position.set(-3, 5, 4);
    scene.add(key);
    const rim = new THREE.PointLight(0xff8fd0, 2.4, 30);
    rim.position.set(3, 2, -3);
    scene.add(rim);

    camera = new THREE.PerspectiveCamera(34, 1, 0.1, 100);
    return { renderer, scene, camera };
  } catch {
    return null;
  }
}

function frame(obj: THREE.Object3D, cam: THREE.PerspectiveCamera, yaw = 0.5) {
  const box = new THREE.Box3().setFromObject(obj);
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  obj.position.sub(center); // recenter at origin
  const maxDim = Math.max(size.x, size.y, size.z) || 1;
  const fov = (cam.fov * Math.PI) / 180;
  const dist = (maxDim / 2) / Math.tan(fov / 2) * 1.55;
  cam.position.set(Math.sin(yaw) * dist, dist * 0.28, Math.cos(yaw) * dist);
  cam.lookAt(0, 0, 0);
  cam.near = Math.max(0.01, dist / 20);
  cam.far = dist * 20;
  cam.updateProjectionMatrix();
}

function capture(build: () => THREE.Object3D, yaw = 0.5): string {
  const env = ensure();
  if (!env) return "";
  const { renderer: r, scene: s, camera: cam } = env;
  const obj = build();
  s.add(obj);
  frame(obj, cam, yaw);
  r.render(s, cam);
  const url = r.domElement.toDataURL("image/png");
  s.remove(obj);
  obj.traverse((o) => {
    if (o instanceof THREE.Mesh) {
      o.geometry.dispose();
      const m = o.material;
      if (Array.isArray(m)) m.forEach((mm) => mm.dispose());
      else (m as THREE.Material).dispose();
    }
  });
  return url;
}

const hatCache = new Map<string, string>();
export function hatThumbnail(id: HatId): string {
  if (id === "none") return "";
  const cached = hatCache.get(id);
  if (cached != null) return cached;
  const url = capture(() => buildHat(id), 0.55);
  hatCache.set(id, url);
  return url;
}

const colorCache = new Map<string, string>();
export function colorThumbnail(color: string): string {
  const cached = colorCache.get(color);
  if (cached != null) return cached;
  const url = capture(() => new THREE.Mesh(
    new THREE.SphereGeometry(1, 40, 30),
    new THREE.MeshStandardMaterial({ color, roughness: 0.32, metalness: 0.05, envMapIntensity: 1.1 }),
  ), 0.4);
  colorCache.set(color, url);
  return url;
}

const patternCache = new Map<string, string>();
export function patternThumbnail(id: PatternId, color: string): string {
  const key = `${id}|${color}`;
  const cached = patternCache.get(key);
  if (cached != null) return cached;
  const url = capture(() => new THREE.Mesh(
    new THREE.SphereGeometry(1, 40, 30),
    new THREE.MeshStandardMaterial({ map: patternTexture(id, color), roughness: 0.34, metalness: 0.04, envMapIntensity: 1.0 }),
  ), 0.4);
  patternCache.set(key, url);
  return url;
}

const patTexCache = new Map<string, THREE.CanvasTexture>();
function patternTexture(id: PatternId, color: string): THREE.CanvasTexture {
  const key = `${id}|${color}`;
  const cached = patTexCache.get(key);
  if (cached) return cached;
  const c = document.createElement("canvas");
  c.width = c.height = 128;
  const ctx = c.getContext("2d")!;
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, 128, 128);
  ctx.fillStyle = "#ffffff";
  if (id === "stripes") {
    ctx.save(); ctx.translate(64, 64); ctx.rotate(-Math.PI / 4); ctx.translate(-64, -64);
    for (let x = -64; x < 192; x += 24) ctx.fillRect(x, -64, 12, 256);
    ctx.restore();
  } else if (id === "dots" || id === "star") {
    ctx.fillStyle = id === "star" ? "#ffd166" : "#ffffff";
    for (let y = 12; y < 128; y += 26) for (let x = 12; x < 128; x += 26) {
      ctx.beginPath(); ctx.arc(x, y, 6, 0, Math.PI * 2); ctx.fill();
    }
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  patTexCache.set(key, tex);
  return tex;
}
